// Download of an HM-reviewed job: the JD (schema 2), the screening questions and a printable pre-screen scorecard.
// The recruiter brief is never included.
import { pdfSafe } from './reportExport';
import { API_BASE } from './jobIntake';

export const SCORE_LEGEND = [
  'Score each answer: ✓ = 2 · ~ = 1 · ✗ = 0',
  '≥14 Shortlist · 10–13 Maybe · <10 Not now',
];
export const SCORECARD_HEAD = ['#', 'Skill', 'Question', 'Expected answer', 'Red flags', '✓', '~', '✗', 'Notes'];

// One block list feeds both the PDF and the DOCX
// "TAL-00001-029 – Senior Data Engineer" (the Job ID first, when the job has one)
export const displayTitle = (reqId, jobTitle) => (reqId && jobTitle ? `${reqId} – ${jobTitle}` : (jobTitle || reqId || ''));

export function jobBlocks(jobTitle, jd, questions, reqId) {
  const b = [{ t: 'title', text: displayTitle(reqId, jobTitle || jd.title) || 'Job description' }];
  if ((jd.focus_tags || []).length) b.push({ t: 'p', text: jd.focus_tags.map((t) => `${t.label}: ${t.value}`).join(' · ') });
  if ((jd.summary || []).length) { b.push({ t: 'h', text: 'Job summary' }); jd.summary.forEach((p) => b.push({ t: 'p', text: p })); }
  if ((jd.responsibilities || []).length) {
    b.push({ t: 'h', text: 'Key responsibilities' });
    jd.responsibilities.forEach((g) => { b.push({ t: 'sub', text: g.heading }); b.push({ t: 'bullets', items: g.bullets }); });
  }
  if ((jd.required || []).length) b.push({ t: 'h', text: 'Required qualifications' }, { t: 'bullets', items: jd.required });
  if ((jd.preferred || []).length) b.push({ t: 'h', text: 'Preferred qualifications' }, { t: 'bullets', items: jd.preferred });
  if ((jd.skills_table || []).length) {
    b.push({ t: 'h', text: 'Technical skills summary' },
      { t: 'table', head: ['Category', 'Skills'], rows: jd.skills_table.map((r) => [r.category, r.skills]) });
  }
  const locked = jd.locked || {};
  const lockedLines = [locked.line, locked.work_authorization, locked.interview ? `Interview process: ${locked.interview}` : null].filter(Boolean);
  if (lockedLines.length) { b.push({ t: 'h', text: 'Location, work mode & compensation' }); lockedLines.forEach((l) => b.push({ t: 'p', text: l })); }
  if (jd.eeo) b.push({ t: 'note', text: jd.eeo });

  b.push({ t: 'page', landscape: true }, { t: 'h', text: `Screening questions & scorecard (${questions.length})` },
    { t: 'p', text: 'Candidate: ____________________   Date: __________   Recruiter: ____________________' },
    { t: 'table', head: SCORECARD_HEAD, scorecard: true,
      rows: questions.map((q, i) => [String(i + 1), q.skill || '', q.question, q.expected_answer || '—', q.red_flags || '—', '☐', '☐', '☐', '']) },
    { t: 'bullets', items: SCORE_LEGEND },
    { t: 'p', text: `Total: ______ / ${questions.length * 2}     Result:  ☐ Shortlist   ☐ Maybe   ☐ Not now` });
  return b;
}

// DejaVu Sans (free licence, public/fonts/DejaVu-LICENSE.txt) so ✓ ~ ✗ ☐ print as in the Word file. Fetched on the
// first PDF download only; if it can't be loaded the built-in font is used with plain-text stand-ins.
export const PDF_FONT = 'DejaVuSans';
const FONT_FILES = [['DejaVuSans.ttf', 'normal'], ['DejaVuSans-Bold.ttf', 'bold']];
let fontData = null;

const toBase64 = (buf) => {
  const bytes = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  return window.btoa(s);
};

async function addPdfFont(doc) {
  try {
    fontData = fontData || await Promise.all(FONT_FILES.map(async ([file]) => {
      const res = await fetch(`${process.env.PUBLIC_URL || ''}/fonts/${file}`);
      if (!res.ok) throw new Error(`${file}: ${res.status}`);
      return toBase64(await res.arrayBuffer());
    }));
    FONT_FILES.forEach(([file, style], i) => { doc.addFileToVFS(file, fontData[i]); doc.addFont(file, PDF_FONT, style); });
    return PDF_FONT;
  } catch (e) {
    return 'helvetica';
  }
}

export async function jobToPdf(blocks) {
  const { jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const font = await addPdfFont(doc);
  const pdfText = (s) => (font === PDF_FONT ? String(s ?? '') : pdfSafe(String(s ?? '').replace(/☐/g, '[  ]')));
  const W = () => doc.internal.pageSize.getWidth();           // the scorecard page is landscape
  const H = () => doc.internal.pageSize.getHeight();
  const M = 15;
  const maxW = () => W() - 2 * M;
  let y = 18;
  const ensure = (h) => { if (y + h > H() - 15) { doc.addPage(); y = 18; } };
  const write = (text, { size = 10, style = 'normal', color = [55, 65, 81], indent = 0, gap = 2 } = {}) => {
    doc.setFont(font, style); doc.setFontSize(size); doc.setTextColor(...color);
    const lh = size * 0.45;
    doc.splitTextToSize(pdfText(text), maxW() - indent).forEach((line) => { ensure(lh); doc.text(line, M + indent, y); y += lh; });
    y += gap;
  };
  blocks.forEach((b) => {
    if (b.t === 'title') write(b.text, { size: 18, style: 'bold', color: [0, 61, 130], gap: 4 });
    else if (b.t === 'page') { doc.addPage('a4', b.landscape ? 'landscape' : 'portrait'); y = 18; }
    else if (b.t === 'h') { y += 3; ensure(14); write(b.text, { size: 13, style: 'bold', color: [0, 61, 130] });
      doc.setDrawColor(0, 169, 157); doc.line(M, y - 1.5, W() - M, y - 1.5); y += 3; }
    else if (b.t === 'sub') write(b.text, { size: 11, style: 'bold', color: [17, 24, 39] });
    else if (b.t === 'p') write(b.text);
    else if (b.t === 'note') write(b.text, { size: 9, color: [107, 114, 128] });
    else if (b.t === 'bullets') b.items.forEach((it) => write(`•  ${it}`, { indent: 2, gap: 1 }));
    else if (b.t === 'table') {
      autoTable(doc, {
        startY: y, margin: { left: M, right: M }, theme: 'grid',
        head: [b.head.map(pdfText)], body: b.rows.map((r) => r.map(pdfText)),
        styles: { font, fontSize: b.scorecard ? 8 : 9, cellPadding: 1.8, textColor: [55, 65, 81], overflow: 'linebreak', valign: 'top' },
        headStyles: { fillColor: [0, 102, 204], textColor: 255, fontStyle: 'bold' },
        columnStyles: b.scorecard ? { 0: { cellWidth: 7 }, 1: { cellWidth: 26 }, 2: { cellWidth: 58 }, 3: { cellWidth: 64 },
          4: { cellWidth: 46 }, 5: { cellWidth: 8, halign: 'center' }, 6: { cellWidth: 8, halign: 'center' },
          7: { cellWidth: 8, halign: 'center' }, 8: { cellWidth: 42 } } : {},
      });
      y = doc.lastAutoTable.finalY + 4;
    }
  });
  return doc;
}

function saveBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

// "TAL-00001-029_Senior_Data_Engineer_v2.pdf" (same rule as the server's Word file name)
export const jobFileName = (reqId, jobTitle, versionNo, ext) =>
  `${[reqId, String(jobTitle || 'job').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_|_$/g, '')].filter(Boolean).join('_')}_v${versionNo || 1}.${ext}`;

// PDF: built here. Word: a native .docx built by the server (python-docx) — real headings, lists and tables.
export async function downloadJob(format, { intakeId, reqId, jobTitle, versionNo, jd, questions }) {
  if (format === 'pdf') {
    (await jobToPdf(jobBlocks(jobTitle, jd || {}, questions || [], reqId))).save(jobFileName(reqId, jobTitle, versionNo, 'pdf'));
    return;
  }
  const res = await fetch(`${API_BASE}/api/job-intakes/${intakeId}/versions/${versionNo}/docx`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });
  if (!res.ok) throw new Error('The Word file could not be created — please try again');
  saveBlob(await res.blob(), jobFileName(reqId, jobTitle, versionNo, 'docx'));
}
